<template>
  <div v-loading="isLoading" class="order-detail">
    <header class="header">
      <span>{{ $t('trade.order-detail') }}</span>
      <icon name="cross" @click.native="close" />
    </header>
    <SimpleBar class="items">
      <li v-if="!this.isOPT">
        <span>{{ $t('trade.stock-name') }}:</span>
        <span>{{ stockName | namePrecise(25) }}</span>
      </li>
      <li class="symbol">
        <span>{{ $t('trade.symbol') }}:</span>
        <span>{{ stockSymbol }}</span>
      </li>
      <li>
        <span>{{ $t('trade.call-direction') }}:</span>
        <OrderActionName :action="order.action" />
      </li>
      <li class="type">
        <span>{{ $t('trade.type') }}:</span>
        <OrderTypeName
          :is-attach="!!order.parentId"
          :order-type="order.orderType"
          :action="order.action"
        />
      </li>
      <li class="quantity">
        <span>{{ $t('trade.order-detail-page.total-quantity') }}:</span>
        <span>{{ order.totalQuantity }}</span>
      </li>
      <li class="filled">
        <span>{{ $t('trade.order-detail-page.filled-quantity') }}:</span>
        <span>{{ order.filledQuantity }}</span>
      </li>
      <li v-if="order.trailingPercent">
        <span>{{ $t('trade.order-trail1-price-percent-label') }}</span>
        <span>{{ order.trailingPercent }}%</span>
      </li>
      <li v-if="order.trailingPrice">
        <span>{{ $t('trade.order-trail2-price-gap-label') }}：</span>
        <span>{{ order.trailingPrice | floatNum }}</span>
      </li>
      <li v-if="isLMT" class="price">
        <span>{{ $t('trade.limit-price-label') }}:</span>
        <span>{{ order.limitPrice | floatNum(order) }}</span>
      </li>
      <li v-if="isSTP" class="price">
        <span>{{ $t('trade.stop-loss-price') }}:</span>
        <span>{{ order.stpPrice | floatNum(order) }}</span>
      </li>
      <li>
        <span>{{ $t('trade.order-detail-page.order-avg-price') }}:</span>
        <span>{{ order.avgFillPrice | floatNum(order) }}</span>
      </li>
      <li class="currency">
        <span>{{ $t('trade.currency') }}:</span>
        <span>{{ order.currency }}</span>
      </li>
      <li v-if="isIb">
        <span>{{ $t('trade.expire-time') }}:</span>
        <TimeInForce :time-in-force="order.timeInForce" />
      </li>
      <li v-if="isUS && isSTK">
        <span>{{ $t('trade.before-after-market-open') }}:</span>
        <span>{{ order.outsideRth ? 'ETH' : 'RTH' }}</span>
      </li>
      <li v-if="order.status === 6">
        <span>{{ $t('trade.commission') }}:</span>
        <span>
          {{ Math.abs(order.commission) | float }}
          {{ order.currency }}
        </span>
      </li>
      <li>
        <span>{{ $t('trade.order-detail-page.open-date') }}:</span>
        <span>{{ order.openTime | formatDate('1-5') }}</span>
      </li>
      <li>
        <span>{{ $t('trade.order-detail-page.recent-date') }}:</span>
        <span>{{ order.latestTime | formatDate('1-5') }}</span>
      </li>
      <li>
        <span>{{ $t('trade.order-detail-page.status') }}:</span>
        <OrderStatusName
          :status="order.status"
          :remark="calculateRemark(order)"
        />
      </li>
    </SimpleBar>
    <div v-if="message" class="cancel-notice">
      <p>{{ $t('trade.order-detail-page.notice') }}</p>
      <p>{{ message }}</p>
    </div>
  </div>
</template>
<script>
import Icon from 'com/Icon'
import SimpleBar from 'com/SimpleBar'
import OrderActionName from 'mod/TextFields/OrderActionName'
import OrderTypeName from 'mod/TextFields/OrderTypeName'
import OrderStatusName from 'mod/TextFields/OrderStatusName'
import TimeInForce from 'mod/TextFields/TimeInForce'
import { mapGetters } from 'vuex'
import { MARKET_TYPE, SEC_TYPE } from 'const/stock'
import { ORDER_TYPE } from 'const/trade'
export default {
  name: 'Detail',
  components: {
    Icon,
    SimpleBar,
    TimeInForce,
    OrderTypeName,
    OrderActionName,
    OrderStatusName
  },
  data() {
    return {
      isLoading: false,
      detail: null
    }
  },
  watch: {
    curAccountId() {
      this.close()
    },
    language() {
      this.getOrderDetail()
    },
    orderId() {
      this.getOrderDetail()
    }
  },
  created() {
    this.getOrderDetail()
  },
  computed: {
    ...mapGetters(['curAccountId', 'orderDetail', 'isIb', 'language']),
    order() {
      return {
        ...this.orderDetail,
        ...this.detail
      }
    },
    orderId() {
      return this.order.orderId || this.order.id
    },
    isOPT() {
      return this.order.secType === SEC_TYPE.OPT
    },
    isSTK() {
      return this.order.secType === SEC_TYPE.STK
    },
    isUS() {
      return this.order.market === MARKET_TYPE.US
    },
    isTRAIL() {
      return [
        ORDER_TYPE.TRAIL,
        ORDER_TYPE.TRAIL_1,
        ORDER_TYPE.TRAIL_2
      ].includes(this.order.orderType)
    },
    isLMT() {
      return [ORDER_TYPE.LMT, ORDER_TYPE.STP_LMT].includes(this.order.orderType)
    },
    isSTP() {
      return [ORDER_TYPE.STP, ORDER_TYPE.STP_LMT].includes(this.order.orderType)
    },
    stockName() {
      return this.order.nameCN
    },
    stockSymbol() {
      if (this.isOPT) {
        return this.$options.filters.formatOptionName(this.order, 0)
      }
      return this.order.symbol
    },
    message() {
      return this.isIb ? this.order.remark : this.order.message
    }
  },
  methods: {
    getOrderDetail() {
      if (!this.orderId) return
      this.isLoading = true
      this.$store
        .dispatch('getOrderDetail', this.orderId)
        .then(order => {
          this.detail = order
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    close() {
      this.$store.dispatch('setOrderDetail', null)
    },
    calculateRemark(order) {
      return this.isIb ? order.remark : order.messageCode
    }
  }
}
</script>
<style lang="less" scoped>
@import '~uikit';
.order-detail {
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 43px;
  width: 320px;
  z-index: 2002;
  text-align: center;
  background-color: var(--bg-block);
  .header {
    position: relative;
    line-height: 50px;
    font-size: 14px;
    text-align: center;
    border-bottom: 2px solid var(--special-color);
    span {
      color: var(--active-color);
    }
    svg {
      position: absolute;
      top: 16px;
      right: 10px;
      color: @light-grey;
      line-height: 50px;
      cursor: pointer;
      user-select: none;
      &:hover {
        color: var(--active-color);
      }
    }
  }
  .items {
    flex: 1;
    min-height: 0;
    li {
      display: flex;
      justify-content: space-between;
      line-height: 30px;
      margin: 0;
      padding: 0;
      list-style: none;
      font-size: 12px;
      padding: 0 10px;
      span:first-child {
        color: @light-grey;
      }
      &:nth-child(2n) {
        background-color: var(--special-color);
      }
    }
    .order-status-name {
      color: var(--active-color);
    }
  }
  .action-container {
    display: flex;
    justify-content: space-around;
    align-items: center;
    margin: 20px 20px 0;
    button {
      margin-bottom: 10px;
    }
  }
  .cancel-notice {
    background-color: var(--special-color);
    color: var(--default-font-color);
    padding: 10px;
    p {
      text-align: left;
      font-size: 12px;
      margin: 0;
    }
    p:first-child {
      margin-bottom: 10px;
    }
  }
}
</style>
